import React, { useState, useEffect } from 'react';
import { X, Calendar, MapPin, Users, DollarSign, Image as ImageIcon, Tag, Clock, Globe, Building, Plus } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import { useSystemPreset } from '../../../../contexts/SystemPresetContext';
import type { CreateEventData, EventType, CreateAgendaItemData } from '../../../../types/communityEvents';

interface CreateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (eventData: CreateEventData, agendaItems: CreateAgendaItemData[]) => Promise<void>;
  loading?: boolean;
}

const CreateEventModal: React.FC<CreateEventModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false
}) => {
  const { language } = useLocalization();
  const { presetData } = useSystemPreset();
  
  // Form state
  const [formData, setFormData] = useState<CreateEventData>({
    title: '',
    description: '',
    communityEventTypeId: '',
    startDate: '',
    endDate: '',
    // Location fields (flattened)
    locationType: 'physical',
    address: '',
    city: '',
    country: '',
    venueName: '',
    latitude: 0.0,
    longitude: 0.0,
    isOnline: false,
    meetingLink: '',
    registrationDeadline: '',
    isRegistrationRequired: false,
    isFree: true,
    currency: 'SAR',
    tags: [],
    imageURL: '',
    requirements: ''
  });

  // Separate agenda state
  const [agendaItems, setAgendaItems] = useState<CreateAgendaItemData[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState(1);
  const [tagInput, setTagInput] = useState('');

  // Get event types from preset data and transform them
  const eventTypes: EventType[] = (presetData?.eventTypes || []).map(type => ({
    id: type.id,
    name: type.name_en,
    nameAr: type.name_ar,
    icon: '', // Default empty icon
    color: '', // Default empty color
    description: '',
    descriptionAr: '',
    isActive: true,
    order: 0
  }));

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: '',
        description: '',
        communityEventTypeId: '',
        startDate: '',
        endDate: '',
        // Location fields (flattened)
        locationType: 'physical',
        address: '',
        city: '',
        country: '',
        venueName: '',
        latitude: 0.0,
        longitude: 0.0,
        isOnline: false,
        meetingLink: '',
        registrationDeadline: '',
        isRegistrationRequired: false,
        isFree: true,
        currency: 'SAR',
        tags: [],
        imageURL: '',
        requirements: ''
      });
      setErrors({});
      setCurrentStep(1);
      setTagInput('');
      setAgendaItems([]);
    }
  }, [isOpen]);

  // Step-by-step validation functions
  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = language === 'ar' ? 'العنوان مطلوب' : 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = language === 'ar' ? 'الوصف مطلوب' : 'Description is required';
    }

    if (!formData.communityEventTypeId) {
      newErrors.communityEventTypeId = language === 'ar' ? 'نوع الفعالية مطلوب' : 'Event type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.startDate) {
      newErrors.startDate = language === 'ar' ? 'تاريخ البداية مطلوب' : 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = language === 'ar' ? 'تاريخ النهاية مطلوب' : 'End date is required';
    }

    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      newErrors.endDate = language === 'ar' ? 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية' : 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.isOnline && !formData.meetingLink) {
      newErrors.meetingLink = language === 'ar' ? 'رابط الاجتماع مطلوب للفعاليات الأونلاين' : 'Meeting link is required for online events';
    }

    if (!formData.isOnline && !formData.address && !formData.venueName) {
      newErrors.location = language === 'ar' ? 'العنوان أو اسم المكان مطلوب' : 'Address or venue name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep4 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.isRegistrationRequired && formData.maxAttendees && formData.maxAttendees < 1) {
      newErrors.maxAttendees = language === 'ar' ? 'عدد المشاركين يجب أن يكون أكبر من 0' : 'Max attendees must be greater than 0';
    }

    if (!formData.isFree && (!formData.price || formData.price <= 0)) {
      newErrors.price = language === 'ar' ? 'السعر مطلوب للفعاليات المدفوعة' : 'Price is required for paid events';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep5 = (): boolean => {
    // Step 5 (Tags & Final Details) doesn't have required fields
    // All fields are optional, so always return true
    return true;
  };

  // Complete form validation (for final submission)
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = language === 'ar' ? 'العنوان مطلوب' : 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = language === 'ar' ? 'الوصف مطلوب' : 'Description is required';
    }

    if (!formData.communityEventTypeId) {
      newErrors.communityEventTypeId = language === 'ar' ? 'نوع الفعالية مطلوب' : 'Event type is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = language === 'ar' ? 'تاريخ البداية مطلوب' : 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = language === 'ar' ? 'تاريخ النهاية مطلوب' : 'End date is required';
    }

    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      newErrors.endDate = language === 'ar' ? 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية' : 'End date must be after start date';
    }

    if (formData.isOnline && !formData.meetingLink) {
      newErrors.meetingLink = language === 'ar' ? 'رابط الاجتماع مطلوب للفعاليات الأونلاين' : 'Meeting link is required for online events';
    }

    if (!formData.isOnline && !formData.address && !formData.venueName) {
      newErrors.location = language === 'ar' ? 'العنوان أو اسم المكان مطلوب' : 'Address or venue name is required';
    }

    if (formData.isRegistrationRequired && formData.maxAttendees && formData.maxAttendees < 1) {
      newErrors.maxAttendees = language === 'ar' ? 'عدد المشاركين يجب أن يكون أكبر من 0' : 'Max attendees must be greater than 0';
    }

    if (!formData.isFree && (!formData.price || formData.price <= 0)) {
      newErrors.price = language === 'ar' ? 'السعر مطلوب للفعاليات المدفوعة' : 'Price is required for paid events';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate current step and proceed to next
  const handleNextStep = () => {
    let isValid = false;
    
    switch (currentStep) {
      case 1:
        isValid = validateStep1();
        break;
      case 2:
        isValid = validateStep2();
        break;
      case 3:
        isValid = validateStep3();
        break;
      case 4:
        isValid = validateStep4();
        break;
      case 5:
        isValid = validateStep5();
        break;
      default:
        isValid = true;
    }
    
    if (isValid) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // If we're on step 6, submit with agenda items
      // If we're on step 5, submit without agenda items (empty array)
      const agendaToSubmit = currentStep === 6 ? agendaItems : [];
      await onSubmit(formData, agendaToSubmit);
      onClose();
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  // Handle input changes
  const handleInputChange = (field: keyof CreateEventData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle location type change
  const handleLocationTypeChange = (type: 'physical' | 'online' | 'hybrid') => {
    setFormData(prev => ({
      ...prev,
      locationType: type,
      isOnline: type === 'online'
    }));
  };

  // Handle tag addition
  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  // Handle agenda item addition
  const handleAddAgendaItem = () => {
    const newItem: CreateAgendaItemData = {
      title: '',
      description: '',
      startTime: '09:00',
      endTime: '10:00',
      speakerName: '',
      speakerTitle: ''
    };
    
    setAgendaItems(prev => [...prev, newItem]);
  };

  // Handle agenda item change
  const handleAgendaItemChange = (index: number, field: string, value: string) => {
    setAgendaItems(prev => 
      prev.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  // Handle agenda item removal
  const handleRemoveAgendaItem = (index: number) => {
    setAgendaItems(prev => prev.filter((_, i) => i !== index));
  };

  // Handle tag removal
  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle key press for tag input
  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
            {language === 'ar' ? 'إنشاء فعالية جديدة' : 'Create New Event'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3, 4, 5, 6].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep
                    ? 'bg-primary text-white'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                }`}>
                  {step}
                </div>
                {step < 6 && (
                  <div className={`w-8 h-0.5 mx-2 ${
                    step < currentStep ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-700'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-2">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {language === 'ar' ? 'الخطوة' : 'Step'} {currentStep} {language === 'ar' ? 'من 6' : 'of 6'}
            </p>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 pb-8 space-y-6 min-h-0">
            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
                  {language === 'ar' ? 'المعلومات الأساسية' : 'Basic Information'}
                </h3>
                
                {/* Step 1 Errors */}
                {(errors.title || errors.description || errors.communityEventTypeId) && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                          {language === 'ar' ? 'يرجى تصحيح الأخطاء التالية' : 'Please correct the following errors'}
                        </h4>
                        <ul className="text-sm text-red-700 dark:text-red-300 list-disc list-inside space-y-1">
                          {errors.title && <li>{errors.title}</li>}
                          {errors.description && <li>{errors.description}</li>}
                          {errors.communityEventTypeId && <li>{errors.communityEventTypeId}</li>}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Event Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'ar' ? 'عنوان الفعالية' : 'Event Title'} *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white ${
                      errors.title ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                    }`}
                    placeholder={language === 'ar' ? 'أدخل عنوان الفعالية' : 'Enter event title'}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                  )}
                </div>

                {/* Event Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'ar' ? 'وصف الفعالية' : 'Event Description'} *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={4}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white ${
                      errors.description ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                    }`}
                    placeholder={language === 'ar' ? 'أدخل وصف الفعالية' : 'Enter event description'}
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-500">{errors.description}</p>
                  )}
                </div>

                {/* Event Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'ar' ? 'نوع الفعالية' : 'Event Type'} *
                  </label>
                  <select
                    value={formData.communityEventTypeId}
                    onChange={(e) => handleInputChange('communityEventTypeId', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white ${
                      errors.communityEventTypeId ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                    }`}
                  >
                    <option value="">
                      {language === 'ar' ? 'اختر نوع الفعالية' : 'Select event type'}
                    </option>
                    {eventTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {language === 'ar' ? type.nameAr : type.name}
                      </option>
                    ))}
                  </select>
                  {errors.communityEventTypeId && (
                    <p className="mt-1 text-sm text-red-500">{errors.communityEventTypeId}</p>
                  )}
                </div>

                {/* Event Image URL */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'ar' ? 'صورة الفعالية' : 'Event Image URL'}
                  </label>
                  <div className="relative">
                    <input
                      type="url"
                      value={formData.imageURL}
                      onChange={(e) => handleInputChange('imageURL', e.target.value)}
                      className="w-full px-4 py-3 pl-10 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                      placeholder={language === 'ar' ? 'أدخل رابط الصورة' : 'Enter image URL'}
                    />
                    <ImageIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Date & Time */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
                  {language === 'ar' ? 'التاريخ والوقت' : 'Date & Time'}
                </h3>
                
                {/* Step 2 Errors */}
                {(errors.startDate || errors.endDate) && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                          {language === 'ar' ? 'يرجى تصحيح الأخطاء التالية' : 'Please correct the following errors'}
                        </h4>
                        <ul className="text-sm text-red-700 dark:text-red-300 list-disc list-inside space-y-1">
                          {errors.startDate && <li>{errors.startDate}</li>}
                          {errors.endDate && <li>{errors.endDate}</li>}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {language === 'ar' ? 'تاريخ البداية' : 'Start Date'} *
                    </label>
                    <div className="relative">
                      <input
                        type="datetime-local"
                        value={formData.startDate}
                        onChange={(e) => handleInputChange('startDate', e.target.value)}
                        className={`w-full px-4 py-3 pl-10 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white ${
                          errors.startDate ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                        }`}
                      />
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    </div>
                    {errors.startDate && (
                      <p className="mt-1 text-sm text-red-500">{errors.startDate}</p>
                    )}
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {language === 'ar' ? 'تاريخ النهاية' : 'End Date'} *
                    </label>
                    <div className="relative">
                      <input
                        type="datetime-local"
                        value={formData.endDate}
                        onChange={(e) => handleInputChange('endDate', e.target.value)}
                        className={`w-full px-4 py-3 pl-10 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white ${
                          errors.endDate ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                        }`}
                      />
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    </div>
                    {errors.endDate && (
                      <p className="mt-1 text-sm text-red-500">{errors.endDate}</p>
                    )}
                  </div>
                </div>

                {/* Registration Deadline */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'ar' ? 'آخر موعد للتسجيل' : 'Registration Deadline'}
                  </label>
                  <div className="relative">
                    <input
                      type="datetime-local"
                      value={formData.registrationDeadline}
                      onChange={(e) => handleInputChange('registrationDeadline', e.target.value)}
                      className="w-full px-4 py-3 pl-10 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                    />
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Location */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
                  {language === 'ar' ? 'الموقع' : 'Location'}
                </h3>
                
                {/* Step 3 Errors */}
                {(errors.meetingLink || errors.location) && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                          {language === 'ar' ? 'يرجى تصحيح الأخطاء التالية' : 'Please correct the following errors'}
                        </h4>
                        <ul className="text-sm text-red-700 dark:text-red-300 list-disc list-inside space-y-1">
                          {errors.meetingLink && <li>{errors.meetingLink}</li>}
                          {errors.location && <li>{errors.location}</li>}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Location Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                    {language === 'ar' ? 'نوع الموقع' : 'Location Type'}
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      type="button"
                      onClick={() => handleLocationTypeChange('physical')}
                      className={`p-4 border rounded-lg text-center transition-colors ${
                        formData.locationType === 'physical'
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-slate-300 dark:border-slate-600 hover:border-slate-400'
                      }`}
                    >
                      <Building className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-sm font-medium">
                        {language === 'ar' ? 'حضوري' : 'Physical'}
                      </span>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => handleLocationTypeChange('online')}
                      className={`p-4 border rounded-lg text-center transition-colors ${
                        formData.locationType === 'online'
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-slate-300 dark:border-slate-600 hover:border-slate-400'
                      }`}
                    >
                      <Globe className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-sm font-medium">
                        {language === 'ar' ? 'أونلاين' : 'Online'}
                      </span>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => handleLocationTypeChange('hybrid')}
                      className={`p-4 border rounded-lg text-center transition-colors ${
                        formData.locationType === 'hybrid'
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-slate-300 dark:border-slate-600 hover:border-slate-400'
                      }`}
                    >
                      <MapPin className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-sm font-medium">
                        {language === 'ar' ? 'مختلط' : 'Hybrid'}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Physical Location Fields */}
                {formData.locationType !== 'online' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {language === 'ar' ? 'اسم المكان' : 'Venue Name'}
                      </label>
                      <input
                        type="text"
                        value={formData.venueName}
                        onChange={(e) => handleInputChange('venueName', e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                        placeholder={language === 'ar' ? 'أدخل اسم المكان' : 'Enter venue name'}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {language === 'ar' ? 'العنوان' : 'Address'} *
                      </label>
                      <textarea
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        rows={3}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white ${
                          errors.location ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                        }`}
                        placeholder={language === 'ar' ? 'أدخل العنوان الكامل' : 'Enter full address'}
                      />
                      {errors.location && (
                        <p className="mt-1 text-sm text-red-500">{errors.location}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'ar' ? 'المدينة' : 'City'}
                        </label>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                          placeholder={language === 'ar' ? 'أدخل المدينة' : 'Enter city'}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'ar' ? 'البلد' : 'Country'}
                        </label>
                        <input
                          type="text"
                          value={formData.country}
                          onChange={(e) => handleInputChange('country', e.target.value)}
                          className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                          placeholder={language === 'ar' ? 'أدخل البلد' : 'Enter country'}
                        />
                      </div>
                    </div>

                    {/* Coordinates */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'ar' ? 'خط العرض (Latitude)' : 'Latitude'}
                        </label>
                        <input
                          type="number"
                          step="any"
                          value={formData.latitude}
                          onChange={(e) => handleInputChange('latitude', parseFloat(e.target.value) || 0.0)}
                          className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                          placeholder="24.7136"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'ar' ? 'خط الطول (Longitude)' : 'Longitude'}
                        </label>
                        <input
                          type="number"
                          step="any"
                          value={formData.longitude}
                          onChange={(e) => handleInputChange('longitude', parseFloat(e.target.value) || 0.0)}
                          className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                          placeholder="46.6753"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Online Meeting Link */}
                {formData.locationType === 'online' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {language === 'ar' ? 'رابط الاجتماع' : 'Meeting Link'} *
                    </label>
                    <div className="relative">
                      <input
                        type="url"
                        value={formData.meetingLink}
                        onChange={(e) => handleInputChange('meetingLink', e.target.value)}
                        className={`w-full px-4 py-3 pl-10 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white ${
                          errors.meetingLink ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                        }`}
                        placeholder={language === 'ar' ? 'أدخل رابط الاجتماع' : 'Enter meeting link'}
                      />
                      <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    </div>
                    {errors.meetingLink && (
                      <p className="mt-1 text-sm text-red-500">{errors.meetingLink}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Registration & Pricing */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
                  {language === 'ar' ? 'التسجيل والتسعير' : 'Registration & Pricing'}
                </h3>
                
                {/* Step 4 Errors */}
                {(errors.maxAttendees || errors.price) && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                          {language === 'ar' ? 'يرجى تصحيح الأخطاء التالية' : 'Please correct the following errors'}
                        </h4>
                        <ul className="text-sm text-red-700 dark:text-red-300 list-disc list-inside space-y-1">
                          {errors.maxAttendees && <li>{errors.maxAttendees}</li>}
                          {errors.price && <li>{errors.price}</li>}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Registration Required */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="isRegistrationRequired"
                    checked={formData.isRegistrationRequired}
                    onChange={(e) => handleInputChange('isRegistrationRequired', e.target.checked)}
                    className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary"
                  />
                  <label htmlFor="isRegistrationRequired" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {language === 'ar' ? 'يتطلب التسجيل' : 'Registration Required'}
                  </label>
                </div>

                {/* Max Attendees */}
                {formData.isRegistrationRequired && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {language === 'ar' ? 'الحد الأقصى للمشاركين' : 'Maximum Attendees'}
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        value={formData.maxAttendees || ''}
                        onChange={(e) => handleInputChange('maxAttendees', e.target.value ? parseInt(e.target.value) : undefined)}
                        className={`w-full px-4 py-3 pl-10 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white ${
                          errors.maxAttendees ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                        }`}
                        placeholder={language === 'ar' ? 'أدخل العدد الأقصى' : 'Enter maximum number'}
                      />
                      <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    </div>
                    {errors.maxAttendees && (
                      <p className="mt-1 text-sm text-red-500">{errors.maxAttendees}</p>
                    )}
                  </div>
                )}

                {/* Free/Paid Event */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                    {language === 'ar' ? 'نوع الفعالية' : 'Event Type'}
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => handleInputChange('isFree', true)}
                      className={`p-4 border rounded-lg text-center transition-colors ${
                        formData.isFree
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-slate-300 dark:border-slate-600 hover:border-slate-400'
                      }`}
                    >
                      <span className="text-sm font-medium">
                        {language === 'ar' ? 'مجاني' : 'Free'}
                      </span>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => handleInputChange('isFree', false)}
                      className={`p-4 border rounded-lg text-center transition-colors ${
                        !formData.isFree
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-slate-300 dark:border-slate-600 hover:border-slate-400'
                      }`}
                    >
                      <span className="text-sm font-medium">
                        {language === 'ar' ? 'مدفوع' : 'Paid'}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Price */}
                {!formData.isFree && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {language === 'ar' ? 'السعر' : 'Price'} *
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.price || ''}
                          onChange={(e) => handleInputChange('price', e.target.value ? parseFloat(e.target.value) : undefined)}
                          className={`w-full px-4 py-3 pl-10 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white ${
                            errors.price ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                          }`}
                          placeholder={language === 'ar' ? 'أدخل السعر' : 'Enter price'}
                        />
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      </div>
                      {errors.price && (
                        <p className="mt-1 text-sm text-red-500">{errors.price}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {language === 'ar' ? 'العملة' : 'Currency'}
                      </label>
                      <select
                        value={formData.currency}
                        onChange={(e) => handleInputChange('currency', e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                      >
                        <option value="SAR">SAR</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="AED">AED</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Requirements */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'ar' ? 'متطلبات المشاركة' : 'Participation Requirements'}
                  </label>
                  <textarea
                    value={formData.requirements}
                    onChange={(e) => handleInputChange('requirements', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                    placeholder={language === 'ar' ? 'أدخل متطلبات المشاركة' : 'Enter participation requirements'}
                  />
                </div>
              </div>
            )}

            {/* Step 5: Tags & Final Details */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
                  {language === 'ar' ? 'العلامات والتفاصيل النهائية' : 'Tags & Final Details'}
                </h3>
                
                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'ar' ? 'العلامات' : 'Tags'}
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary/10 text-primary border border-primary/20"
                      >
                        #{tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-2 text-primary hover:text-primary-dark"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={handleTagKeyPress}
                      className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                      placeholder={language === 'ar' ? 'أدخل علامة جديدة' : 'Enter new tag'}
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                    >
                      <Tag className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Choice Explanation */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                        {language === 'ar' ? 'اختر الخطوة التالية' : 'Choose Next Step'}
                      </h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        {language === 'ar' 
                          ? 'يمكنك إنشاء الفعالية الآن أو إضافة جدول أعمال للفعالية'
                          : 'You can create the event now or add an agenda for the event'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 6: Event Agenda */}
            {currentStep === 6 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
                  {language === 'ar' ? 'جدول أعمال الفعالية' : 'Event Agenda'}
                </h3>
                
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  {language === 'ar' 
                    ? 'أضف جدول أعمال للفعالية (اختياري). يمكنك إضافة عناصر متعددة مع أوقات البداية والنهاية.' 
                    : 'Add an agenda for your event (optional). You can add multiple items with start and end times.'
                  }
                </p>

                {/* Agenda Items List */}
                <div className="space-y-4">
                  {agendaItems.map((item, index) => (
                    <div key={index} className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-medium text-slate-800 dark:text-white">
                          {language === 'ar' ? `عنصر ${index + 1}` : `Item ${index + 1}`}
                        </h4>
                        <button
                          type="button"
                          onClick={() => handleRemoveAgendaItem(index)}
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            {language === 'ar' ? 'العنوان' : 'Title'} *
                          </label>
                          <input
                            type="text"
                            value={item.title}
                            onChange={(e) => handleAgendaItemChange(index, 'title', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white text-sm"
                            placeholder={language === 'ar' ? 'عنوان العنصر' : 'Item title'}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            {language === 'ar' ? 'المتحدث' : 'Speaker'} (اختياري)
                          </label>
                          <input
                            type="text"
                            value={item.speakerName || ''}
                            onChange={(e) => handleAgendaItemChange(index, 'speakerName', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white text-sm"
                            placeholder={language === 'ar' ? 'اسم المتحدث' : 'Speaker name'}
                          />
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          {language === 'ar' ? 'الوصف' : 'Description'} (اختياري)
                        </label>
                        <textarea
                          value={item.description || ''}
                          onChange={(e) => handleAgendaItemChange(index, 'description', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white text-sm"
                          placeholder={language === 'ar' ? 'وصف العنصر' : 'Item description'}
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            {language === 'ar' ? 'وقت البداية' : 'Start Time'} *
                          </label>
                          <input
                            type="time"
                            value={item.startTime}
                            onChange={(e) => handleAgendaItemChange(index, 'startTime', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white text-sm"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            {language === 'ar' ? 'وقت النهاية' : 'End Time'} *
                          </label>
                          <input
                            type="time"
                            value={item.endTime}
                            onChange={(e) => handleAgendaItemChange(index, 'endTime', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white text-sm"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            {language === 'ar' ? 'منصب المتحدث' : 'Speaker Title'} (اختياري)
                          </label>
                          <input
                            type="text"
                            value={item.speakerTitle || ''}
                            onChange={(e) => handleAgendaItemChange(index, 'speakerTitle', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white text-sm"
                            placeholder={language === 'ar' ? 'منصب المتحدث' : 'Speaker title'}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {agendaItems.length === 0 && (
                    <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                      <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>{language === 'ar' ? 'لا توجد عناصر في جدول الأعمال' : 'No agenda items yet'}</p>
                      <p className="text-sm mt-1">
                        {language === 'ar' ? 'انقر على "إضافة عنصر" لبدء إنشاء جدول الأعمال' : 'Click "Add Item" to start creating the agenda'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Add Agenda Item Button */}
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={handleAddAgendaItem}
                    className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>{language === 'ar' ? 'إضافة عنصر' : 'Add Item'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
            {currentStep < 6 && (
              <button
                type="button"
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {language === 'ar' ? 'السابق' : 'Previous'}
              </button>
            )}

            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>

              {currentStep < 5 ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNextStep();
                  }}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  {language === 'ar' ? 'التالي' : 'Next'}
                </button>
              ) : currentStep === 5 ? (
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      // Create event without agenda
                      handleSubmit(e as any);
                    }}
                    className="px-6 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 transition-colors"
                  >
                    {loading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{language === 'ar' ? 'جاري الإنشاء...' : 'Creating...'}</span>
                      </div>
                    ) : (
                      language === 'ar' ? 'إنشاء الفعالية بدون جدول أعمال' : 'Create Event without Agenda'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentStep(6);
                    }}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    {language === 'ar' ? 'إضافة جدول أعمال' : 'Add Agenda Items'}
                  </button>
                </div>
              ) : currentStep === 6 ? (
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentStep(currentStep - 1);
                    }}
                    className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                  >
                    {language === 'ar' ? 'السابق' : 'Previous'}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{language === 'ar' ? 'جاري الإنشاء...' : 'Creating...'}</span>
                      </div>
                    ) : (
                      language === 'ar' ? 'إنشاء الفعالية مع جدول الأعمال' : 'Create Event with Agenda'
                    )}
                  </button>
                </div>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{language === 'ar' ? 'جاري الإنشاء...' : 'Creating...'}</span>
                    </div>
                  ) : (
                    language === 'ar' ? 'إنشاء الفعالية' : 'Create Event'
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEventModal;
